import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

function toCSV(rows) {
  if (!rows.length) return '';
  const headers = Object.keys(rows[0]);
  const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
}

function downloadCSV(rows, filename) {
  const csv = toCSV(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function downloadJSON(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const TABS = [
  { id: 'executive', label: 'EXECUTIVE SUMMARY', icon: '📊', endpoint: '/admin/reports/executive' },
  { id: 'members',   label: 'MEMBERS',            icon: '👥', endpoint: '/admin/reports/members' },
  { id: 'contributions', label: 'CONTRIBUTIONS',  icon: '💡', endpoint: '/admin/reports/contributions' },
  { id: 'quests',    label: 'QUESTS',             icon: '🎯', endpoint: '/admin/reports/quests' },
  { id: 'pods',      label: 'PODS',               icon: '🚀', endpoint: '/admin/reports/pods' },
];

function KVTable({ data }) {
  if (!data || typeof data !== 'object') return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {Object.entries(data).filter(([k]) => k !== 'generatedAt' && k !== 'programme').map(([section, val]) => (
        <div key={section} className="card" style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 2, marginBottom: 10, textTransform: 'uppercase' }}>{section}</div>
          {typeof val === 'object' && !Array.isArray(val) ? (
            Object.entries(val).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--brd2)', fontSize: 12 }}>
                <span style={{ color: 'var(--txt2)', fontFamily: 'Share Tech Mono', fontSize: 11 }}>{k}</span>
                <span style={{ color: 'var(--j1)', fontFamily: 'Orbitron', fontWeight: 700, fontSize: 13 }}>{String(v)}</span>
              </div>
            ))
          ) : (
            <div style={{ fontFamily: 'Orbitron', fontSize: 20, fontWeight: 800, color: 'var(--j1)' }}>{String(val)}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function DataTable({ rows }) {
  if (!rows?.length) return <div className="empty-state">No data</div>;
  const keys = Object.keys(rows[0]);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table" style={{ fontSize: 11 }}>
        <thead>
          <tr>{keys.map(k => <th key={k} style={{ fontSize: 9, letterSpacing: 1 }}>{k.toUpperCase()}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {keys.map(k => (
                <td key={k} style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {typeof row[k] === 'number' ? (
                    <span style={{ fontFamily: 'Orbitron', fontSize: 11, color: k.toLowerCase().includes('point') ? 'var(--j1)' : 'var(--txt)' }}>
                      {row[k].toLocaleString()}
                    </span>
                  ) : String(row[k] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Email Blast Modal ────────────────────────────────────────────────────────
function BlastModal({ onClose }) {
  const [roles, setRoles] = useState(['member']);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const ROLE_OPTIONS = ['member', 'sme', 'cwg', 'stakeholder', 'intern'];
  const toggleRole = r => setRoles(p => p.includes(r) ? p.filter(x => x !== r) : [...p, r]);

  const send = async () => {
    if (!subject.trim() || !body.trim() || !roles.length) return;
    setSending(true);
    try {
      const { data } = await api.post('/admin/mail-blast', { roles, subject, body });
      setResult(data);
    } catch (err) {
      setResult({ error: err.response?.data?.error || 'Failed' });
    }
    setSending(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <div className="modal-title">📧 SEND EMAIL BLAST</div>

        {result ? (
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            {result.error ? (
              <div style={{ color: 'var(--j6)', fontFamily: 'Share Tech Mono' }}>{result.error}</div>
            ) : (
              <>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                <div style={{ fontFamily: 'Orbitron', fontSize: 16, color: 'var(--j5)' }}>BLAST SENT</div>
                <div style={{ fontSize: 13, color: 'var(--txt2)', marginTop: 8, fontFamily: 'Share Tech Mono' }}>
                  Delivered to {result.sent} / {result.total} recipients
                  {result.fallback && <span style={{ color: 'var(--j4)' }}> (console fallback — mail not configured)</span>}
                </div>
                <button className="btn btn-primary" style={{ marginTop: 20 }} onClick={onClose}>CLOSE</button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="form-group">
              <label className="label">Target Roles (select one or more)</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {ROLE_OPTIONS.map(r => (
                  <button key={r} onClick={() => toggleRole(r)}
                    style={{ padding: '5px 14px', borderRadius: 3, border: `1px solid ${roles.includes(r) ? 'var(--j1)' : 'var(--brd2)'}`,
                      background: roles.includes(r) ? 'rgba(0,212,255,0.1)' : 'transparent',
                      color: roles.includes(r) ? 'var(--j1)' : 'var(--txt2)',
                      fontFamily: 'Share Tech Mono', fontSize: 11, cursor: 'pointer', textTransform: 'uppercase' }}>
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="label">Subject</label>
              <input className="input" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email subject line" />
            </div>
            <div className="form-group">
              <label className="label">Message Body</label>
              <textarea className="input" rows={6} value={body} onChange={e => setBody(e.target.value)} placeholder="Email body — plain text, line breaks become paragraph breaks" />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={onClose}>CANCEL</button>
              <button className="btn btn-primary" onClick={send} disabled={sending || !subject.trim() || !body.trim() || !roles.length}>
                {sending ? 'SENDING…' : `📧 SEND TO ${roles.join(', ').toUpperCase()}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Reports() {
  const { can } = useAuth();
  const [tab, setTab] = useState('executive');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [blast, setBlast] = useState(false);

  const current = TABS.find(t => t.id === tab);

  useEffect(() => {
    if (data[tab]) return;
    setLoading(true);
    api.get(current.endpoint)
      .then(r => setData(p => ({ ...p, [tab]: r.data })))
      .catch(() => setData(p => ({ ...p, [tab]: null })))
      .finally(() => setLoading(false));
  }, [tab]); // eslint-disable-line

  const handleDownloadCSV = () => {
    const d = data[tab];
    if (!d) return;
    const rows = Array.isArray(d) ? d : [d];
    downloadCSV(rows, `neuralops-${tab}-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const handleDownloadJSON = () => {
    const d = data[tab];
    if (!d) return;
    downloadJSON(d, `neuralops-${tab}-${new Date().toISOString().slice(0, 10)}.json`);
  };

  const handlePrint = () => {
    window.print();
  };

  if (!can('cwg', 'stakeholder', 'sme')) {
    return <div className="empty-state">Access restricted</div>;
  }

  const tabData = data[tab];
  const isExecutive = tab === 'executive';

  return (
    <div>
      <style>{`@media print { .no-print { display:none!important } .print-only { display:block } }`}</style>

      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="page-title">REPORTS & EXPORTS</div>
          <div className="page-sub">Executive data, member stats, contribution and quest summaries</div>
        </div>
        <div className="no-print" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {can('cwg') && (
            <button className="btn btn-primary" onClick={() => setBlast(true)} style={{ fontSize: 11 }}>
              📧 EMAIL BLAST
            </button>
          )}
          {!isExecutive && (
            <button className="btn" onClick={handleDownloadCSV} disabled={!tabData} style={{ fontSize: 11 }}>
              ⬇ CSV (Excel)
            </button>
          )}
          <button className="btn" onClick={handleDownloadJSON} disabled={!tabData} style={{ fontSize: 11 }}>
            ⬇ JSON
          </button>
          <button className="btn" onClick={handlePrint} style={{ fontSize: 11 }}>
            🖨 PRINT / PDF
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="no-print" style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.filter(t => can('cwg', 'stakeholder') || t.id !== 'members').map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '7px 16px', borderRadius: 3, border: `1px solid ${tab === t.id ? 'var(--j1)' : 'var(--brd2)'}`,
              background: tab === t.id ? 'rgba(0,212,255,0.08)' : 'transparent',
              color: tab === t.id ? 'var(--j1)' : 'var(--txt2)',
              fontFamily: 'Share Tech Mono', fontSize: 10, letterSpacing: 1, cursor: 'pointer' }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="empty-state">Loading report…</div>
      ) : tabData === null ? (
        <div className="empty-state">Failed to load report data</div>
      ) : tabData ? (
        <div>
          {/* Executive summary header for print */}
          <div style={{ marginBottom: 16, fontFamily: 'Share Tech Mono', fontSize: 10, color: 'var(--txt3)' }}>
            {current.icon} {current.label} · Generated {new Date().toLocaleString()} · NeuralOps AI CoE
          </div>
          {isExecutive ? <KVTable data={tabData} /> : <DataTable rows={tabData} />}
        </div>
      ) : null}

      {blast && <BlastModal onClose={() => setBlast(false)} />}
    </div>
  );
}
